import React, { useMemo, useReducer, useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { JobPostFiltersState } from "./contexts/FilterJobsContext";
import { useJobPosts } from "@/hooks";
import { FilterJobsContext, MIN_SALARY, MAX_SALARY } from "./contexts";
import { defaultJobPostsFilters } from "@/hooks/jobPosts/useJobPosts";

dayjs.extend(utc);

export type FilterJobsAction =
  | { type: "SET_FILTERS"; payload: Partial<JobPostFiltersState> }
  | { type: "RESET_FILTERS" };

function filterJobsReducer(
  state: JobPostFiltersState,
  action: FilterJobsAction,
): JobPostFiltersState {
  switch (action.type) {
    case "SET_FILTERS":
      return { ...state, ...action.payload };
    case "RESET_FILTERS":
      return defaultJobPostsFilters;
    default:
      return state;
  }
}

export function FilterJobsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  /* --------------------- Hooks --------------------- */
  const [hydrated, setHydrated] = useState(false);
  const [page, setPage] = useState(1);

  const [filters, dispatch] = useReducer(
    filterJobsReducer,
    defaultJobPostsFilters,
  );

  const valueSkip = (value: unknown) => {
    return value === undefined || value === "";
  };

  const skipFetch =
    !hydrated ||
    Object.values(filters).every(value => {
      if (Array.isArray(value)) return value.length === 0;
      return valueSkip(value);
    });

  const { jobPosts, loading, error, pagination } = useJobPosts({
    skip: skipFetch,
    filters: filters,
    page,
  });

  /* --------------------- Constants --------------------- */

  const noMatchingResults = !skipFetch && jobPosts?.length === 0;
  if (noMatchingResults === false) {
  }

  /* --------------------- Lifecycle Methods --------------------- */
  useEffect(() => {
    if (!hydrated) {
      // stores the filters in session storage
      // so that the filters persist from home
      // page to search page on search button trigger
      const savedFilters = sessionStorage.getItem("filters");

      if (savedFilters) {
        dispatch({ type: "SET_FILTERS", payload: JSON.parse(savedFilters) });
      }
      setHydrated(true);
    }
  }, [hydrated]);

  useEffect(() => {
    if (hydrated) {
      sessionStorage.setItem("filters", JSON.stringify(filters));
    }
  }, [filters, hydrated]);

  function resetFilters() {
    dispatch({ type: "RESET_FILTERS" });
  }

  const value = useMemo(() => {
    const getNoResultsMessage = () => {
      const activeFilters: string[] = [];

      if (filters.selectedCompanies.length > 0) {
        activeFilters.push(
          `for companies ${filters.selectedCompanies?.map(c => c.company_name).join(", ")}`,
        );
      }

      if (filters.selectedHealthcareDomains.length > 0) {
        activeFilters.push(
          `in domains ${filters.selectedHealthcareDomains?.map(d => d.value).join(", ")}`,
        );
      }

      if (filters.selectedDepartments.length > 0) {
        activeFilters.push(
          `in departments ${filters.selectedDepartments?.map(d => d.dept_name).join(", ")}`,
        );
      }

      if (filters.selectedJobRoles.length > 0) {
        activeFilters.push(
          `for job roles ${filters.selectedJobRoles?.map(r => r.role_name).join(", ")}`,
        );
      }

      if (filters.selectedJobSettings.length > 0) {
        activeFilters.push(
          `for job settings ${filters.selectedJobSettings?.map(s => s.setting_name).join(", ")}`,
        );
      }

      if (
        (filters.selectedDatePostedOption?.label !== "" &&
          filters.selectedDatePostedOption !== null) ||
        (filters.selectedDatePostedOption?.label !== undefined &&
          filters.selectedDatePostedOption !== null)
      ) {
        activeFilters.push(
          `posted in ${filters.selectedDatePostedOption.label}`,
        );
      }

      if (
        (filters.selectedDateUpdatedOption?.label !== "" &&
          filters.selectedDateUpdatedOption !== null) ||
        (filters.selectedDateUpdatedOption?.label !== undefined &&
          filters.selectedDateUpdatedOption !== null)
      ) {
        activeFilters.push(
          `updated in ${filters.selectedDateUpdatedOption.label}`,
        );
      }

      if (filters.selectedCompanySizes?.length > 0) {
        activeFilters.push(
          `for company size ${filters.selectedCompanySizes?.map(s => s.size_range).join(", ")}`,
        );
      }

      if (
        filters.selectedSalaryRangeMin &&
        filters.selectedSalaryRangeMax &&
        (filters.selectedSalaryRangeMin !== MIN_SALARY ||
          filters.selectedSalaryRangeMax !== MAX_SALARY)
      ) {
        activeFilters.push(
          `with salary range between ${filters.selectedSalaryRangeMin} and ${filters.selectedSalaryRangeMax}`,
        );
      }

      if (filters.selectedJobSalaryCurrencies.length > 0) {
        activeFilters.push(
          `with salary currency ${filters.selectedJobSalaryCurrencies?.map(c => c.currency_code).join(", ")}`,
        );
      }

      if (filters.selectedLocations.length > 0) {
        activeFilters.push(
          `in locations ${filters.selectedLocations?.map(loc => loc.name).join(", ")}`,
        );
      }

      if (filters.search && filters.search.trim() !== "") {
        activeFilters.push(`with search query "${filters.search}"`);
      }

      let message = "No matching job posts";
      if (activeFilters.length > 0) {
        message += ` ${activeFilters.join(", ")}.`;
      }

      return message;
    };

    return {
      jobPosts,
      filters,

      loading,
      error,
      dispatch,

      resetFilters,
      noMatchingResults,
      getNoResultsMessage,

      page,
      setPage,
      pagination,
    };
  }, [
    jobPosts,
    filters,
    dispatch,
    loading,
    error,
    noMatchingResults,
    page,
    pagination,
  ]);

  return (
    <FilterJobsContext.Provider value={value}>
      {children}
    </FilterJobsContext.Provider>
  );
}
